import { GetProfileResponseDto } from '@shared/core/users'
import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormattedText, Loader } from 'src/modules/core/components'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { FirstConfigurationScreen } from '../../components'
import { SendTestOrFinishScreen } from '../../components/send-test-or-finish'
import { useProfile } from '../../hooks/use-profile'

/**
 * Checks if the user has completed the initial configuration steps
 * by comparing created_at and updated_at timestamps of the default message template.
 * If they differ, it means the user has modified their configuration.
 */
function hasCompletedConfigurationSteps(profile: GetProfileResponseDto | null): boolean {
  if (!profile?.default_message_template) {
    return false
  }

  const { created_at, updated_at } = profile.default_message_template

  if (!created_at || !updated_at) {
    return false
  }

  const created = typeof created_at === 'string' ? new Date(created_at) : created_at
  const updated = typeof updated_at === 'string' ? new Date(updated_at) : updated_at

  // If updated_at is after created_at, the user has completed their configuration
  return updated.getTime() > created.getTime()
}

export function ConfigurationWrapper() {
  const profile = useProfile()
  const navigate = useNavigate()
  const steps = useMemo(() => [FirstConfigurationScreen], [])
  const [Step] = steps

  // Redirect booking users to booking onboarding
  useEffect(() => {
    if (profile.data?.is_booking_user) {
      navigate(routeNames.bookingOnboarding.path, { replace: true })
    }
  }, [profile.data?.is_booking_user, navigate])

  // Show loader while profile is loading
  if (profile.isLoading) {
    return <Loader isLoading text={<FormattedText id="configuration.profileLoadingText" />} full />
  }

  // If user is a booking user, don't render SMS Reminder onboarding (redirect will happen)
  if (profile.data?.is_booking_user) {
    return null
  }

  // If user has completed configuration steps, skip to the final flow
  if (hasCompletedConfigurationSteps(profile.data)) {
    return <SendTestOrFinishScreen active={true} sendTestReminder={false} />
  }

  // Otherwise, show the single configuration step
  return (
    <div className="notranslate">
      <Step />
    </div>
  )
}
